import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetAllClientMarketOrdersQueryResponse, GetAllClientMarketOrdersPathParams, GetAllClientMarketOrdersQueryParams } from "../types/GetAllClientMarketOrders";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetAllClientMarketOrdersClient = typeof client<GetAllClientMarketOrdersQueryResponse, never, never>;
type GetAllClientMarketOrders = {
    data: GetAllClientMarketOrdersQueryResponse;
    error: never;
    request: never;
    pathParams: GetAllClientMarketOrdersPathParams;
    queryParams: GetAllClientMarketOrdersQueryParams;
    headerParams: never;
    response: GetAllClientMarketOrdersQueryResponse;
    client: {
        parameters: Partial<Parameters<GetAllClientMarketOrdersClient>[0]>;
        return: Awaited<ReturnType<GetAllClientMarketOrdersClient>>;
    };
};
export const getAllClientMarketOrdersQueryKey = (clientCode: GetAllClientMarketOrdersPathParams["clientCode"], marketCode: GetAllClientMarketOrdersPathParams["marketCode"], params?: GetAllClientMarketOrders["queryParams"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode/orders", params: { clientCode: clientCode, marketCode: marketCode } }, ...(params ? [params] : [])] as const;
export type GetAllClientMarketOrdersQueryKey = ReturnType<typeof getAllClientMarketOrdersQueryKey>;
export function getAllClientMarketOrdersQueryOptions(clientCode: GetAllClientMarketOrdersPathParams["clientCode"], marketCode: GetAllClientMarketOrdersPathParams["marketCode"], params?: GetAllClientMarketOrders["queryParams"], options: GetAllClientMarketOrders["client"]["parameters"] = {}) {
    const queryKey = getAllClientMarketOrdersQueryKey(clientCode, marketCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClientMarketOrders["data"], GetAllClientMarketOrders["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}/orders`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets all orders for a given client market. If an instrument symbol is
provided in the query string then orders will be filtered to only those
for that instrument.
 * @summary Get all
 * @link /api/clients/:clientCode/markets/:marketCode/orders
 */
export function useGetAllClientMarketOrders<TData = GetAllClientMarketOrders["response"], TQueryData = GetAllClientMarketOrders["response"], TQueryKey extends QueryKey = GetAllClientMarketOrdersQueryKey>(clientCode: GetAllClientMarketOrdersPathParams["clientCode"], marketCode: GetAllClientMarketOrdersPathParams["marketCode"], params?: GetAllClientMarketOrders["queryParams"], options: {
    query?: Partial<QueryObserverOptions<GetAllClientMarketOrders["response"], GetAllClientMarketOrders["error"], TData, TQueryData, TQueryKey>>;
    client?: GetAllClientMarketOrders["client"]["parameters"];
} = {}): UseQueryResult<TData, GetAllClientMarketOrders["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientMarketOrdersQueryKey(clientCode, marketCode, params);
    const query = useQuery({
        ...getAllClientMarketOrdersQueryOptions(clientCode, marketCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetAllClientMarketOrders["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getAllClientMarketOrdersSuspenseQueryKey = (clientCode: GetAllClientMarketOrdersPathParams["clientCode"], marketCode: GetAllClientMarketOrdersPathParams["marketCode"], params?: GetAllClientMarketOrders["queryParams"]) => [{ url: "/api/clients/:clientCode/markets/:marketCode/orders", params: { clientCode: clientCode, marketCode: marketCode } }, ...(params ? [params] : [])] as const;
export type GetAllClientMarketOrdersSuspenseQueryKey = ReturnType<typeof getAllClientMarketOrdersSuspenseQueryKey>;
export function getAllClientMarketOrdersSuspenseQueryOptions(clientCode: GetAllClientMarketOrdersPathParams["clientCode"], marketCode: GetAllClientMarketOrdersPathParams["marketCode"], params?: GetAllClientMarketOrders["queryParams"], options: GetAllClientMarketOrders["client"]["parameters"] = {}) {
    const queryKey = getAllClientMarketOrdersSuspenseQueryKey(clientCode, marketCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClientMarketOrders["data"], GetAllClientMarketOrders["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/markets/${marketCode}/orders`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets all orders for a given client market. If an instrument symbol is
provided in the query string then orders will be filtered to only those
for that instrument.
 * @summary Get all
 * @link /api/clients/:clientCode/markets/:marketCode/orders
 */
export function useGetAllClientMarketOrdersSuspense<TData = GetAllClientMarketOrders["response"], TQueryKey extends QueryKey = GetAllClientMarketOrdersSuspenseQueryKey>(clientCode: GetAllClientMarketOrdersPathParams["clientCode"], marketCode: GetAllClientMarketOrdersPathParams["marketCode"], params?: GetAllClientMarketOrders["queryParams"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetAllClientMarketOrders["response"], GetAllClientMarketOrders["error"], TData, TQueryKey>>;
    client?: GetAllClientMarketOrders["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetAllClientMarketOrders["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientMarketOrdersSuspenseQueryKey(clientCode, marketCode, params);
    const query = useSuspenseQuery({
        ...getAllClientMarketOrdersSuspenseQueryOptions(clientCode, marketCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetAllClientMarketOrders["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}